import React, { useEffect, useState, useRef } from "react";
import { clearSession } from "../../shared/_helpers/session";
import Modal from "react-bootstrap/Modal";
import DashboardHeader from "./DashboardHeader";
import DashboardUserHeader from "./DashboardUserHeader";
import { getOnboardingDetails, getOldOnboardingData, getAdvisorListFromClientForChat, createChatFromClientSide, checkBlockedStatus, getDashboardInsights, getPlaidTransactions, getFinancialGoals, uploadDocument } from "../../shared/_services/common.services";
import { CLIENT_ACCESS_DENIED_MESSAGE, CLIENT_ACCESS_DENIED_REDIRECT, isForbiddenClientAccess } from "../../shared/_helpers/apiErrors";
import { setQuickSetup } from "../../shared/_services/auth.services";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import BookAppointmentModal from "../AppointmentBooking";
import AmountPlaceholder from "../../assets/images/AmountPlaceholder.svg";
import RecentActivityPlaceholder from "../../assets/images/RecentActivityPlaceholder.svg";
import FinancialGoalsPlaceholder from "../../assets/images/FinancialGoalsPlaceholder.svg";

import PrioritiesPlaceholder from "../../assets/images/PrioritiesPlaceholder.svg";
import AssetsPlaceholder from "../../assets/images/AssetsPlaceholder.svg";
import DebtPlaceholder from "../../assets/images/DebtPlaceholder.svg";
import ShortGoalsPlaceholder from "../../assets/images/ShortGoalsPlaceholder.svg";
import LongGoalsPlaceholder from "../../assets/images/LongGoalsPlaceholder.svg";
import DashboardCharts from "../DashboardChart";
import Loader from "../Loader";
import { useSnackbar } from "notistack";
import UserQuestionnaireDtls from "../UserQuestionnaireDtls";
import socket, { joinChat } from '../../shared/_helpers/socket';
import moment from "moment";
import dateIcon from "../../assets/images/date_icon.svg";
import detailsIcon from "../../assets/images/details_icon.svg";
import 'chart.js/auto';
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import dummy_assets_screenshot from '../../assets/images/dummy_assets_screenshot.jpg';
import dummy_debt_screenshot from '../../assets/images/dummy_debt_screenshot.jpg';
import NonSubLockBox from "./NonSubLockBox";
import InsightsPanel, { normalizeInsights } from "./InsightsPanel";
import othersImg from "../../assets/images/Other2.0.svg"
import dbOthers from "../../assets/images/dbOther.svg"
import helpIcon from "../../assets/images/help-circle.svg"

import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

Chart.register(ArcElement, ChartDataLabels);

function hasChartBreakdownData(chartData) {
    return Boolean(chartData?.datasets) &&
        Boolean(chartData.datasets[0]?.data) &&
        chartData.datasets[0].data.length > 0;
}

function getNetWorthDisplayValue(hasNetWorthData, onboardingDetails, calculateTotalInvestment, calculateTotalDebt) {
    if (!hasNetWorthData) return "0";
    return (
        calculateTotalInvestment(onboardingDetails["Investments"]) -
        calculateTotalDebt(onboardingDetails["Debts"])
    ).toLocaleString();
}

function shouldShowPercentageChange(percentageChange, totalAmount, additionalCondition = true) {
    return percentageChange !== 0 &&
        !Number.isNaN(percentageChange) &&
        Number.isFinite(percentageChange) &&
        totalAmount !== 0 &&
        additionalCondition;
}

function hasNamedPriorities(priorities) {
    return Array.isArray(priorities) && priorities.some((priority) => priority?.name);
}

function hasPlanEntries(plans) {
    return Array.isArray(plans) && plans.length > 0;
}

function hasNetWorthDataFrom(onboardingDetails) {
    return (onboardingDetails?.["Investments"]?.length || 0) > 0 ||
        (onboardingDetails?.["Debts"]?.length || 0) > 0;
}

function handleDashboardEntryAction(locationState, openAskQuestionPopup, setBookAppointmentPopup) {
    const actionType = locationState?.actionType;
    if (actionType === "MESSAGE") {
        openAskQuestionPopup();
        return;
    }
    if (actionType === "VIDEO_CALL") {
        setBookAppointmentPopup(true);
    }
}

function renderDashboardHeader(isClientDashboardUser, clientId) {
    return isClientDashboardUser
        ? <DashboardHeader />
        : <DashboardUserHeader clientId={clientId} />;
}

function renderWhen(condition, content) {
    return condition ? content : null;
}

function renderLoadingState(showLoader, content) {
    return showLoader ? <Loader /> : content;
}

function getAttachedFilesText(attachedFilesLength) {
    return `${attachedFilesLength} file${attachedFilesLength > 1 ? "s" : ""} attached`;
}

function renderAskAdvisorSendButtonContent(isUploading) {
    if (isUploading) {
        return (
            <div className="send-btn-spinner" style={{
                width: '18px',
                height: '18px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTopColor: '#ffffff',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
            }}></div>
        );
    }
    return (
        <>
            Send<i className="iconoir-send"></i>
        </>
    );
}

function handleSessionExpiredIfUnauthorized(data, navigate, enqueueSnackbar) {
    if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
    }
}

function renderTopPrioritiesContent(onboardingDetails, othersData, quickSetup, handleFullSetupQuestionnaire) {
    if (hasNamedPriorities(onboardingDetails?.["What do you want help with first?"])) {
        return (
            <div className="db-vertical-options selected-top-priorities">
                <>
                    {onboardingDetails["What do you want help with first?"].map((priority, index) => {
                        let imgSrc;
                        try {
                            imgSrc = require(`../../assets/images/db${priority.name.replace(/\s+/g, "")}.svg`);
                        } catch (error) {
                            imgSrc = require('../../assets/images/default1.png');
                        }
                        return (
                            priority?.name && (
                                <div className="item" key={priority?.name}>
                                    <img src={imgSrc} alt={priority?.name} />
                                    <div>
                                        <p>{priority?.name}</p>
                                        <div className="priority-number">Priority {index + 1}</div>
                                    </div>
                                </div>
                            )
                        );
                    })}
                    {othersData?.["What do you want help with first?"] && (
                        <div className="item others" key="others">
                            <img src={dbOthers} alt="Others" />
                            <div>
                                <p>{othersData["What do you want help with first?"]}</p>
                                <div className="priority-number">Others</div>
                            </div>
                        </div>
                    )}
                </>
            </div>
        );
    }

    return (
        <div className={`ast-card-placeholder py-5 ${quickSetup === true ? "d-block text-center" : ""}`}>
            <img src={PrioritiesPlaceholder} alt="No priorities selected" />
            {quickSetup === true && (
                <div className="text-center">
                    <p>Please fill out the questionnaire to see your short term plans</p>
                    <Link onClick={handleFullSetupQuestionnaire} className="as-btn-primary profile-light-btn px-3 py-2">Fill out the questionnaire</Link>
                </div>
            )}
        </div>
    );
}

function renderAssetsBreakdownContent(subscribed, hasInvestmentBreakdownData, onboardingDetails, investmentChartData, pieChartOptions, calculateTotalInvestment) {
    if (subscribed && hasInvestmentBreakdownData) {
        return (
            <>
                <div className="row align-items-center mb-3">
                    <div className="col-5 pl-5">
                        <h1 className="mb-0 big-amnt-h1">
                            $
                            {calculateTotalInvestment(onboardingDetails["Investments"] || []).toLocaleString()}
                        </h1>
                        <small className="text-muted text-14 f500">Total Assets</small>
                    </div>
                    <div className="col-7 d-flex justify-content-center">
                        <div
                            style={{
                                width: "150px",
                                height: "150px",
                                position: "relative",
                            }}
                        >
                            <Pie data={investmentChartData} options={pieChartOptions} />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="assets-breakdown-list mini-scroll-bar">
                            {investmentChartData.labels.map((label, index) => {
                                const amount = investmentChartData.datasets[0].data[index];
                                const total = calculateTotalInvestment(onboardingDetails["Investments"] || []);
                                const percentage = total > 0 ? ((amount / total) * 100).toFixed(1) : 0;

                                return (
                                    <div
                                        key={label}
                                        className="breakdown-item d-flex justify-content-between align-items-center"
                                        style={{
                                            backgroundColor: investmentChartData.datasets[0].backgroundColor[index] + "1A",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="color-indicator mr-2"
                                                style={{
                                                    backgroundColor: investmentChartData.datasets[0].backgroundColor[index],
                                                }}
                                            ></div>
                                            <span className="breakdown-title">{label}</span>
                                        </div>
                                        <div className="text-end">
                                            <div className="breakdown-amount">
                                                ${amount.toLocaleString()} ({percentage}%)
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (subscribed && !hasInvestmentBreakdownData) {
        return (
            <div className="ast-card-placeholder py-5">
                <img height={160} width={160} src={AssetsPlaceholder} alt="" />
            </div>
        );
    }

    return <img style={{ width: '100%', height: '100%' }} src={dummy_assets_screenshot} alt="" />;
}

function renderDebtBreakdownContent(subscribed, hasDebtBreakdownData, onboardingDetails, debtChartData, investmentChartData, pieChartOptions, calculateTotalDebt) {
    if (subscribed && hasDebtBreakdownData) {
        return (
            <>
                <div className="row align-items-center mb-3">
                    <div className="col-5 pl-5">
                        <h1 className="mb-0 big-amnt-h1">
                            $
                            {calculateTotalDebt(onboardingDetails["Debts"] || []).toLocaleString()}
                        </h1>
                        <small className="text-muted text-14 f500">Total Debt</small>
                    </div>
                    <div className="col-7 d-flex justify-content-center">
                        <div
                            style={{
                                width: "150px",
                                height: "150px",
                                position: "relative",
                            }}
                        >
                            <Pie data={debtChartData} options={pieChartOptions} />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <div className="debt-breakdown-list mini-scroll-bar">
                            {debtChartData.labels.map((label, index) => {
                                const amount = debtChartData.datasets[0].data[index];
                                const total = calculateTotalDebt(onboardingDetails["Debts"] || []);
                                const percentage = total > 0 ? ((amount / total) * 100).toFixed(1) : 0;

                                return (
                                    <div
                                        key={label}
                                        className="breakdown-item d-flex justify-content-between align-items-center"
                                        style={{
                                            backgroundColor: investmentChartData.datasets[0].backgroundColor[index] + "1A",
                                        }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="color-indicator mr-2"
                                                style={{
                                                    backgroundColor: debtChartData.datasets[0].backgroundColor[index],
                                                }}
                                            ></div>
                                            <span className="breakdown-title">{label}</span>
                                        </div>
                                        <div className="text-end">
                                            <div className="breakdown-amount">
                                                ${amount.toLocaleString()} ({percentage}%)
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (subscribed && !hasDebtBreakdownData) {
        return (
            <div className="ast-card-placeholder py-5">
                <img height={160} width={160} src={DebtPlaceholder} alt="" />
            </div>
        );
    }

    return <img style={{ width: '100%', height: '100%' }} src={dummy_debt_screenshot} alt="" />;
}

function renderPlanOptionsContent(onboardingDetails, othersData, sectionName, quickSetup, handleFullSetupQuestionnaire) {
    if (hasPlanEntries(onboardingDetails?.[sectionName])) {
        return (
            <>
                {onboardingDetails[sectionName].map((term) => {
                    let imgSrc;
                    try {
                        imgSrc = require(`../../assets/images/${term.name.replace(/\s+/g, "")}2.0.svg`);
                    } catch (error) {
                        imgSrc = require(`../../assets/images/default1.png`);
                    }
                    return (
                        <div className="item" key={term?.name}>
                            <img src={imgSrc} alt={term.name} />
                            <p>{term.name}</p>
                        </div>
                    );
                })}
                {othersData?.[sectionName] && (
                    <div className="item others" key="others">
                        <img src={othersImg} alt="Others" />
                        <p>{othersData[sectionName]}</p>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className={`ast-card-placeholder py-5 ${quickSetup === true ? "d-block text-center" : ""}`}>
            <img height={160} width={160} src={sectionName === "Short-term" ? ShortGoalsPlaceholder : LongGoalsPlaceholder} alt="" />
            {quickSetup === true && (
                <div className="text-center">
                    <p>Please fill out the questionnaire to see your short term plans</p>
                    <Link onClick={handleFullSetupQuestionnaire} className="as-btn-primary profile-light-btn px-3 py-2">Fill out the questionnaire</Link>
                </div>
            )}
        </div>
    );
}

function Dashboard() {
    const [onboardingDetails, setOnboardingDetails] = useState({});
    const params = useParams();
    const navigate = useNavigate();
    const [thankYouPopupShow, setThankYouPopupShow] = useState(false);
    const [appointmentDetails, setAppointmentDetails] = useState({});
    const [bookAppointmentPopup, setBookAppointmentPopup] = useState(false);
    const [popupShow, setPopupShow] = useState(false);
    const [currentChartType, setCurrentChartType] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [oldInvestmentData, setOldInvestmentData] = useState([]);
    const [oldDebtData, setOldDebtData] = useState([]);
    const [newInvestmentData, setNewInvestmentData] = useState([]);
    const [newDebtData, setNewDebtData] = useState([]);
    const [investmentChartData, setInvestmentChartData] = useState({
        labels: [], datasets: [{ data: [], backgroundColor: [], }],
    });
    const [debtChartData, setDebtChartData] = useState({
        labels: [], datasets: [{ data: [], backgroundColor: [], }],
    });
    const [oldInvestmentChartData, setOldInvestmentChartData] = useState({
        labels: [], datasets: [{ data: [] }],
    });
    const [oldDebtChartData, setOldDebtChartData] = useState({
        labels: [], datasets: [{ data: [] }],
    });
    const [selectedAdvisorId, setSelectedAdvisorId] = useState(null);
    const [chatId, setChatId] = useState("");
    const [messageInput, setMessageInput] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const sender = JSON.parse(localStorage.getItem('currentUser'))?.user._id;
    const fileInputRef = useRef(null);
    const [askQuestionPopupShow, setAskQuestionPopupShow] = useState(false);
    const askQuestionPopupClose = () => {
        setAskQuestionPopupShow(false);
        setAttachedFiles([]);
        setIsUploading(false);
    };
    const openAskQuestionPopup = () => {
        setAskQuestionPopupShow(true);
    };
    // INT-015: financial insights only; the market array is never read.
    const [financialInsights, setFinancialInsights] = useState([]);
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const currentUserType = currentUser?.user?.userType;
    const isClientDashboardUser = currentUserType === "client";
    const shouldShowQuestionnaireDetails = ["advisor", "super-admin", "admin"].includes(currentUserType);
    const subscribed = !!currentUser?.user?.selected_plan;
    const [plaidActivities, setPlaidActivities] = useState([]);
    const [financialGoals, setFinancialGoals] = useState([]);
    const [othersData, setOthersData] = useState({});
    const quickSetup = JSON.parse(localStorage.getItem("currentUser"))?.user?.quickSetup;

    const location = useLocation();

    useEffect(() => {
        handleDashboardEntryAction(location.state, openAskQuestionPopup, setBookAppointmentPopup);
    }, [location.state]);


    const getData = async () => {
        setShowLoader(true);

        const currentUser = JSON.parse(localStorage.getItem('currentUser')) || {};
        const userId = currentUser.user?._id;

        let data = await getOnboardingDetails(null, userId, 'dashboard');
        let oldData = await getOldOnboardingData(userId);


        if (data.status !== 200) {
            handleSessionExpiredIfUnauthorized(data, navigate, enqueueSnackbar);
            if (isForbiddenClientAccess(data)) {
                enqueueSnackbar(CLIENT_ACCESS_DENIED_MESSAGE, {
                    variant: 'error',
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
                navigate(CLIENT_ACCESS_DENIED_REDIRECT);
            }
            setShowLoader(false);
            return;
        }

        if (data.data.length === 0) {
            JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === "client" && navigate("/dashboard-zero");
            setShowLoader(false);
            return;
        }

        const investmentData = data?.data?.data?.find(item => item?.questionName === "Investments")?.selectedOptions || [];
        setNewInvestmentData(investmentData);

        const investmentQuestion = oldData?.data?.find(question => question.questionName === "Investments");
        const oldInvestmentInfo = investmentQuestion?.allotandDebtOptions || [];
        const oldTotalAssets = investmentQuestion?.totalAssets || 0;
        setOldInvestmentData({ oldInvestmentInfo, oldTotalAssets });

        const debtData = data?.data?.data?.find(item => item?.questionName === "Debts")?.selectedOptions || [];
        setNewDebtData(debtData);

        const debtQuestion = oldData?.data?.find(question => question.questionName === "Debts");
        const oldDebtInfo = debtQuestion?.allotandDebtOptions || [];
        const oldTotalDebt = debtQuestion?.totalAssets || 0;
        const oldTotalDebtupdated = debtQuestion?.updated || false;
        setOldDebtData({ oldDebtInfo, oldTotalDebt, oldTotalDebtupdated });

        updateOnboardingDetails(data);
        calculateTotalInvestment(newInvestmentData);
        calculateTotalDebt(newDebtData);
        setShowLoader(false);
    };

    useEffect(() => {
        const fetchChatData = async () => {
            try {
                const response = await getAdvisorListFromClientForChat();

                // Add defensive checks
                const advisorsData = response?.data?.data;
                if (advisorsData) {
                    const { previouslyChattedAdvisors = [], newAdvisors = [] } = advisorsData;

                    if (previouslyChattedAdvisors.length > 0) {
                        setSelectedAdvisorId(previouslyChattedAdvisors[0]?._id);
                    } else if (newAdvisors.length > 0) {
                        setSelectedAdvisorId(newAdvisors[0]?._id);
                    }
                } else {
                    console.error("Unexpected API response format", response);
                }
            } catch (error) {
                console.error("Error fetching chat data", error);
            }
        };
        const bookedAppointment = localStorage.getItem("bookedAppointment");

        if (bookedAppointment) {
            setAppointmentDetails(JSON.parse(bookedAppointment));
            setThankYouPopupShow(true);
        }
        fetchChatData();
    }, []);

    useEffect(() => {
        if (askQuestionPopupShow && selectedAdvisorId) {
            createChatFromClientSide({ participantId: selectedAdvisorId })
                .then((response) => {
                    const { _id: chattId } = response?.data?.data;
                    setChatId(chattId);
                })
                .catch(console.error);
        }
    }, [askQuestionPopupShow, selectedAdvisorId]);

    useEffect(() => {
        if (chatId) {
            joinChat(chatId);
        }
    }, [chatId]);

    //get insighsts data
    const fetchInsightsData = async () => {
        try {
            const response = await getDashboardInsights();
            // `null` (AI off, nothing stored), a missing field or an AxiosError
            // all normalise to [] and render the empty state.
            setFinancialInsights(normalizeInsights(response?.data));
        } catch (error) {
            console.error("Error fetching insights data", error);
        }
    };

    //get recent activities
    const fetchTransactionsData = async () => {
        try {
            const response = await getPlaidTransactions();
            if (response) {
                setPlaidActivities(response)
            }
        } catch (error) {
        }
    };

    //get financial goals
    const fetchFinancialGoalsData = async () => {
        try {
            const response = await getFinancialGoals();
            if (Array.isArray(response) && response.length > 0) {
                const goals = response[0]?.goals || [];
                setFinancialGoals(goals);
            }
        } catch (error) {
            console.error("Error fetching financial goals", error);
        }
    };


    useEffect(() => {
        fetchInsightsData();
        fetchTransactionsData();
        fetchFinancialGoalsData();
    }, []);


    // File attach handlers
    const handleAttachClick = () => {
        if (isUploading) return; // Prevent attaching during upload
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.xls', '.xlsx', '.csv', '.doc', '.docx'];
        const maxSize = 26214400; // 25 MB

        const currentCount = attachedFiles.length;
        const availableSlots = Math.max(0, 10 - currentCount);
        const limitedFiles = files.slice(0, availableSlots);

        const validFiles = limitedFiles.filter((file) => {
            const lowerName = file.name.toLowerCase();
            const hasAllowedExt = allowedExtensions.some((ext) => lowerName.endsWith(ext));
            const isValidSize = file.size <= maxSize;

            if (!hasAllowedExt) {
                console.warn("File type not allowed:", file.name);
                enqueueSnackbar(`File type not allowed: ${file.name}`, { variant: 'error' });
                return false;
            }

            if (!isValidSize) {
                console.warn("File too large (>25MB):", file.name);
                enqueueSnackbar(`File too large (>25MB): ${file.name}`, { variant: 'error' });
                return false;
            }

            return true;
        });

        setAttachedFiles((prev) => [...prev, ...validFiles]);

        // reset input so same file can be re-selected later if needed
        e.target.value = null;
    };

    const handleRemoveAttachedFile = (indexToRemove) => {
        if (isUploading) return; // Prevent removal during upload
        setAttachedFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    };


    const handleSendMessage = async () => {
        if (!messageInput.trim() && attachedFiles.length === 0) {
            enqueueSnackbar('Please enter a message or attach a file', { variant: 'warning' });
            return;
        }

        if (!chatId) {
            enqueueSnackbar('Chat not initialized', { variant: 'error' });
            return;
        }

        // If there are files to upload, disable send button and show progress
        if (attachedFiles.length > 0) {
            setIsUploading(true);
        }

        // 1) Send text message if present
        if (messageInput.trim()) {
            const textPayload = {
                chatId,
                sender,
                content: {
                    message: messageInput.trim(),
                    type: "text",
                    url: ""
                }
            };

            socket.emit("new message", [textPayload]);
        }

        // 2) Upload attached documents (if any) and send as file messages
        if (attachedFiles.length > 0) {
            try {
                const form = new FormData();
                attachedFiles.forEach((file) => form.append("files", file));

                const res = await uploadDocument(form, "chat");
                const uploaded = res?.data?.data || [];

                uploaded.forEach((doc) => {
                    const filePayload = {
                        chatId,
                        sender,
                        content: {
                            message: doc?.originalname || "file",
                            type: "file",
                            url: doc?.file_url || "",
                            doc_id: doc?.doc_id
                        }
                    };

                    socket.emit("new message", [filePayload]);
                });
            } catch (err) {
                console.error("Error uploading documents from chat:", err);
                enqueueSnackbar('Error uploading files', { variant: 'error' });
            } finally {
                setIsUploading(false);
            }
        }

        // Reset input and local attachments
        setMessageInput('');
        setAttachedFiles([]);
        askQuestionPopupClose();
        enqueueSnackbar('Message sent successfully!', { variant: 'success' });
    };

    const checkBlocked = async () => {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'))?.user;
        if (currentUser?.userType === 'super-admin' || currentUser?.userType === 'advisor' || currentUser?.userType === 'admin') {
            return;
        }
        const userId = currentUser?._id;
        
        try {
            // Call the API to check the user's blocked status
            const response = await checkBlockedStatus(userId);
            if (response.data ? response.data.status === 401 : response.response.status === 401) {
                clearSession();
                navigate("/login");
            } else {
                const { isBlocked, isDeleted } = response.data;

                if (isBlocked || isDeleted) {
                    clearSession();
                    navigate("/login");
                    return;
                }
                // Check if the user does not have a selected plan or has a false payment status
                // if (!currentUser?.selected_plan || currentUser?.payment_status === "false") {
                //     navigate("/package");
                // }
                if (!currentUser?.isPhoneVerified) {
                    navigate("/verify-code");
                }
            }
        } catch (error) {
            console.error("Error checking blocked status:", error);
        }
    };
    const updateOnboardingDetails = (data) => {
        const updatedOnboardingDetails = data?.data?.data?.reduce((acc, item) => {
            acc[item.questionName] = item.selectedOptions || []; // keep old structure
            return acc;
        }, {});

        setOnboardingDetails((prevDetails) => ({
            ...prevDetails,
            ...updatedOnboardingDetails,
        }));

        const updatedOthers = data?.data?.data?.reduce((acc, item) => {
            acc[item.questionName] = item.others || "";
            return acc;
        }, {});

        setOthersData(updatedOthers);

        updateChartData(updatedOnboardingDetails);
    };


    const nonEmptyInvestmentValues = (details) => {
        return details && details.some(item => item?.value?.trim() !== '');
    };

    const calculateTotalInvestment = (details) => {
        const total = (details || []).reduce((total, item) => {
            const value = parseFloat(item?.value) || 0;
            return total + value;
        }, 0);
        return total;
    };

    const newInvestmentTotal = calculateTotalInvestment(newInvestmentData);
    const investmentPercentageChange = ((newInvestmentTotal - oldInvestmentData?.oldTotalAssets) / oldInvestmentData?.oldTotalAssets) * 100;

    const nonEmptyDebtValues = (debtDetails) => {
        return debtDetails && debtDetails.some(item => item?.value?.trim() !== '');
    };

    const calculateTotalDebt = (debtDetails) => {
        const total = (debtDetails || []).reduce((total, item) => {
            const value = parseFloat(item?.value) || 0;
            return total + value;
        }, 0);
        return total;
    };

    const newDebtTotal = calculateTotalInvestment(newDebtData);
    const debtPercentageChange = ((newDebtTotal - oldDebtData?.oldTotalDebt) / oldDebtData?.oldTotalDebt) * 100;

    // Function to update chart data
    const updateChartData = (currentOnboardingDetails = null) => {
        const colors = [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
            '#C9CBCF', '#8DD1E1', '#B39CD0', '#CDDC39', '#F44336', '#FFEB3B',
            '#9C27B0', '#E91E63', '#00BCD4', '#FF5722', '#009688'
        ];

        // Use current onboarding details or fallback to state
        const details = currentOnboardingDetails || onboardingDetails;

        // Old Investment Data
        const oldInvestmentLabels = oldInvestmentData?.oldInvestmentInfo?.map(item => item.name);
        const oldInvestmentValues = oldInvestmentData?.oldInvestmentInfo?.map(item => parseFloat(item.value) || 0);
        setOldInvestmentChartData({
            labels: oldInvestmentLabels,
            datasets: [{
                data: oldInvestmentValues,
                backgroundColor: colors.slice(0, oldInvestmentValues?.length),
            }],
        });

        // Old Debt Data
        const oldDebtLabels = oldDebtData?.oldDebtInfo?.map(item => item.name);
        const oldDebtValues = oldDebtData?.oldDebtInfo?.map(item => parseFloat(item.value) || 0);
        setOldDebtChartData({
            labels: oldDebtLabels,
            datasets: [{
                data: oldDebtValues,
                backgroundColor: colors.slice(0, oldDebtValues?.length),
            }],
        });

        // Investment Data
        const investmentData = details["Investments"] || [];
        const investmentLabels = investmentData?.map(item => item?.name);
        const investmentValues = investmentData?.map(item => parseFloat(item?.value) || 0);
        setInvestmentChartData({
            labels: investmentLabels,
            datasets: [{
                data: investmentValues,
                backgroundColor: colors.slice(0, investmentValues?.length), // Apply dynamic colors
            }],
        });

        // Debt Data
        const debtData = details["Debts"] || [];
        const debtLabels = debtData.map(item => item.name);
        const debtValues = debtData.map(item => parseFloat(item.value) || 0);
        setDebtChartData({
            labels: debtLabels,
            datasets: [{
                data: debtValues,
                backgroundColor: colors.slice(0, debtValues?.length), // Apply dynamic colors
            }],
        });
    };

    // Pie chart options for donut charts
    const pieChartOptions = {
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
            },
            datalabels: {
                display: false,
            }
        },
        layout: {
            padding: {
                top: 10,
                bottom: 10,
                left: 10,
                right: 10,
            },
        },
        responsive: true,
    };

    



    const closePopup = () => {
        setPopupShow(false);
        setCurrentChartType("");
    };

    const handleModalClose = async () => {
       
        setThankYouPopupShow(false);
        window.localStorage.removeItem("bookedAppointment");
        //onClose()
    }

    useEffect(() => {
        checkBlocked();
        getData();

    }, []); //eslint-disable-line

    // Update chart data when old data is loaded
    useEffect(() => {
        if (oldInvestmentData?.oldInvestmentInfo && oldDebtData?.oldDebtInfo) {
            updateChartData();
        }
    }, [oldInvestmentData, oldDebtData]); // eslint-disable-line

    //Added Loader for page load and before unload
    useEffect(() => {
        // Hide loader after page load
        window.addEventListener('load', () => {
            setShowLoader(false);
        });

        // Show loader before page unload
        window.addEventListener('beforeunload', () => {
            setShowLoader(true);
        });

        return () => {
            window.removeEventListener('load', () => { });
            window.removeEventListener('beforeunload', () => { });
        };
    }, []);


    

    const renderTooltip = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Your latest transactions and account updates.
        </Tooltip>
    );

    const [activeTab, setActiveTab] = useState("Overview");

    const tabs = ["Overview", "Investment & Debts", "Goals", "Insights"];

    


    const handleFullSetupQuestionnaire = async (e) => {
        e.preventDefault();
        try {
            const currentUserStr = localStorage.getItem("currentUser");
            if (currentUserStr) {
                const currentUser = JSON.parse(currentUserStr);
                const userId = currentUser?.user?._id;
                if (userId) {
                    const response = await setQuickSetup(userId, false);
                    if (response?.status === 200 || response?.status === 201) {
                        // Update localStorage after successful API call
                        if (currentUser && currentUser.user) {
                            const updatedUser = {
                                ...currentUser,
                                user: {
                                    ...currentUser.user,
                                    quickSetup: false
                                }
                            };
                            localStorage.setItem("currentUser", JSON.stringify(updatedUser));
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Error in handleFullSetupQuestionnaire:", error);
        }
        navigate("/q1");
    };



    const hasNetWorthData = hasNetWorthDataFrom(onboardingDetails);
    const netWorthAmount = getNetWorthDisplayValue(
        hasNetWorthData,
        onboardingDetails,
        calculateTotalInvestment,
        calculateTotalDebt
    );
    const hasInvestmentData = nonEmptyInvestmentValues(onboardingDetails?.["Investments"]);
    const totalInvestmentAmount = calculateTotalInvestment(onboardingDetails["Investments"]).toLocaleString();
    const hasInvestmentBreakdownData = hasChartBreakdownData(investmentChartData);
    const hasDebtBreakdownData = hasChartBreakdownData(debtChartData);
    const hasDebtData = nonEmptyDebtValues(onboardingDetails?.["Debts"]);
    const totalDebtAmount = calculateTotalDebt(onboardingDetails["Debts"]).toLocaleString();

    return (
        <>
            {renderLoadingState(showLoader,
                <div>
                    {renderDashboardHeader(isClientDashboardUser, params?.clientId ? params?.clientId : null)}
                    {renderWhen(isClientDashboardUser, (
                        <div className="dashboard-content">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="appointment-banner mb-4">
                                        <div className="d-flex gap-15 place-items-center">
                                            <svg width="39" height="35" viewBox="0 0 39 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M0.625 4.51562C0.625 3.38431 1.07441 2.29933 1.87437 1.49937C2.67433 0.699413 3.75931 0.25 4.89062 0.25H33.1094C34.2407 0.25 35.3257 0.699413 36.1256 1.49937C36.9256 2.29933 37.375 3.38431 37.375 4.51562V13.5364L37.1991 13.4524L36.5232 13.1401C35.9661 12.8831 35.3632 12.7403 34.75 12.7201V4.51562C34.75 3.61 34.015 2.875 33.1094 2.875H4.89062C3.985 2.875 3.25 3.61 3.25 4.51562V27.4844C3.25 28.39 3.985 29.125 4.89062 29.125H8.5V24.5312C8.5 23.661 8.8457 22.8264 9.46106 22.2111C10.0764 21.5957 10.911 21.25 11.7812 21.25H26.2188C26.6497 21.25 27.0763 21.3349 27.4744 21.4998C27.8725 21.6647 28.2343 21.9064 28.5389 22.2111C28.8436 22.5157 29.0853 22.8775 29.2502 23.2756C29.4151 23.6737 29.5 24.1003 29.5 24.5312V25.513L28.5721 25.2702C27.8578 25.0835 27.1054 25.1028 26.4016 25.3257C25.6979 25.5487 25.0716 25.9663 24.5952 26.5302L23.3667 27.9858C22.9314 28.5002 22.6147 29.1041 22.4389 29.7546C22.2631 30.4051 22.2326 31.0864 22.3495 31.75H4.89062C4.33046 31.75 3.77577 31.6397 3.25824 31.4253C2.74071 31.2109 2.27047 30.8967 1.87437 30.5006C1.47827 30.1045 1.16407 29.6343 0.949701 29.1168C0.735334 28.5992 0.625 28.0445 0.625 27.4844V4.51562ZM19 18.625C20.7405 18.625 22.4097 17.9336 23.6404 16.7029C24.8711 15.4722 25.5625 13.803 25.5625 12.0625C25.5625 10.322 24.8711 8.65282 23.6404 7.42211C22.4097 6.1914 20.7405 5.5 19 5.5C17.2595 5.5 15.5903 6.1914 14.3596 7.42211C13.1289 8.65282 12.4375 10.322 12.4375 12.0625C12.4375 13.803 13.1289 15.4722 14.3596 16.7029C15.5903 17.9336 17.2595 18.625 19 18.625ZM31.5344 18.2483L32.1631 16.4397C32.28 16.0965 32.4678 15.7818 32.7142 15.5159C32.9607 15.2501 33.2603 15.039 33.5936 14.8964C33.9269 14.7538 34.2864 14.6829 34.6489 14.6883C35.0114 14.6937 35.3687 14.7753 35.6976 14.9277L36.3736 15.2401C37.4761 15.748 38.4001 16.6589 38.5786 17.869C39.4606 23.7949 34.7972 31.9915 29.2992 34.1794C28.177 34.6257 26.9367 34.2713 25.9523 33.556L25.3486 33.1176C25.0536 32.903 24.8059 32.6301 24.6208 32.3158C24.4356 32.0015 24.317 31.6526 24.2722 31.2906C24.2274 30.9286 24.2574 30.5612 24.3604 30.2113C24.4634 29.8614 24.6371 29.5363 24.8708 29.2563L26.1006 27.7994C26.5836 27.2271 27.3475 26.9856 28.0733 27.1746L30.7259 27.8703C32.7314 26.5595 33.7919 24.6958 33.9074 22.279L31.9872 20.2945C31.7304 20.0281 31.5522 19.6959 31.4723 19.3345C31.3924 18.9732 31.4139 18.5982 31.5344 18.2483Z" fill="#F0F1FF" />
                                            </svg>
                                            <span>
                                                <h4>Connect with your advisor</h4>
                                                <p>
                                                    Message your advisor anytime or book a video call. They typically respond within one business day.
                                                </p>
                                            </span>
                                        </div>

                                        <div className="d-flex gap-10">
                                            <button onClick={openAskQuestionPopup} className="as-btn-secondary px-4">
                                                Message Your Advisor
                                            </button>
                                            <button
                                                onClick={() => setBookAppointmentPopup(true)}
                                                type="button"
                                                className="as-btn-secondary px-4"
                                            >
                                                Book a Call
                                            </button>
                                            {bookAppointmentPopup && <BookAppointmentModal onClose={() => setBookAppointmentPopup(false)} />}

                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="ast-mini-card">
                                        <div>
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <small>Net Worth</small>
                                                    <h6>Your total assets minus your total debts.</h6>
                                                </div>
                                                <i>
                                                    <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M19.9199 0.62C19.8185 0.375651 19.6243 0.181475 19.3799 0.0799999C19.2597 0.028759 19.1306 0.00157999 18.9999 0H13.9999C13.7347 0 13.4804 0.105357 13.2928 0.292893C13.1053 0.48043 12.9999 0.734784 12.9999 1C12.9999 1.26522 13.1053 1.51957 13.2928 1.70711C13.4804 1.89464 13.7347 2 13.9999 2H16.5899L10.9999 7.59L7.70994 4.29C7.61698 4.19627 7.50637 4.12188 7.38452 4.07111C7.26266 4.02034 7.13195 3.9942 6.99994 3.9942C6.86793 3.9942 6.73722 4.02034 6.61536 4.07111C6.4935 4.12188 6.3829 4.19627 6.28994 4.29L0.289939 10.29C0.196211 10.383 0.121816 10.4936 0.0710475 10.6154C0.0202789 10.7373 -0.00585938 10.868 -0.00585938 11C-0.00585937 11.132 0.0202789 11.2627 0.0710475 11.3846C0.121816 11.5064 0.196211 11.617 0.289939 11.71C0.382902 11.8037 0.493503 11.8781 0.615362 11.9289C0.737222 11.9797 0.867927 12.0058 0.999939 12.0058C1.13195 12.0058 1.26266 11.9797 1.38452 11.9289C1.50637 11.8781 1.61698 11.8037 1.70994 11.71L6.99994 6.41L10.2899 9.71C10.3829 9.80373 10.4935 9.87812 10.6154 9.92889C10.7372 9.97966 10.8679 10.0058 10.9999 10.0058C11.132 10.0058 11.2627 9.97966 11.3845 9.92889C11.5064 9.87812 11.617 9.80373 11.7099 9.71L17.9999 3.41V6C17.9999 6.26522 18.1053 6.51957 18.2928 6.70711C18.4804 6.89464 18.7347 7 18.9999 7C19.2652 7 19.5195 6.89464 19.707 6.70711C19.8946 6.51957 19.9999 6.26522 19.9999 6V1C19.9984 0.869323 19.9712 0.740222 19.9199 0.62Z" fill="url(#paint0_linear_1449_1495)" />
                                                        <defs>
                                                            <linearGradient id="paint0_linear_1449_1495" x1="9.99704" y1="0" x2="9.99704" y2="12.0058" gradientUnits="userSpaceOnUse">
                                                                <stop offset="0.461538" stopColor="#63363A" />
                                                                <stop offset="1" stopColor="#1A1D43" />
                                                            </linearGradient>
                                                        </defs>
                                                    </svg>

                                                </i>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-end mt-3">
                                                {subscribed && hasNetWorthData && (
                                                    <h2>${netWorthAmount}</h2>
                                                )}
                                                {subscribed && !hasNetWorthData && (
                                                    <div className="ast-mini-card-placeholder">
                                                        <img height={38} width={38} src={AmountPlaceholder} alt="" />
                                                    </div>
                                                )}
                                                {!subscribed && <h2>$0.00</h2>}
                                            </div>
                                        </div>
                                        {!subscribed && (
                                            <NonSubLockBox />
                                        )}
                                    </div>

                                </div>


                                <div className="col-md-4">
                                    <div className="ast-mini-card">
                                        <div>
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <small>Total Assets</small>
                                                    <h6>Everything you own that has financial value.</h6>
                                                </div>
                                                <i className="iconoir-piggy-bank"></i>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-end mt-3">
                                                {subscribed && hasInvestmentData && (
                                                    <>
                                                        <h2>
                                                            ${totalInvestmentAmount}
                                                        </h2>
                                                        {shouldShowPercentageChange(investmentPercentageChange, newInvestmentTotal) && (
                                                            <span className={investmentPercentageChange > 0 ? 'price-up' : 'price-down'}>
                                                                {investmentPercentageChange.toFixed(2)}%
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                                {subscribed && !hasInvestmentData && (
                                                    <div className="ast-mini-card-placeholder">
                                                        <img height={38} width={38} src={AmountPlaceholder} alt="" />
                                                    </div>
                                                )}
                                                {!subscribed && <h2>$0.00</h2>}
                                            </div>
                                        </div>
                                        {!subscribed && (
                                            <NonSubLockBox />
                                        )}
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="ast-mini-card">
                                        <div>
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <small>Total Debt</small>
                                                    <h6>Everything you owe.</h6>
                                                </div>
                                                <i className="iconoir-dollar-circle"></i>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-end mt-3">
                                                {subscribed && hasDebtData && (
                                                    <>
                                                        <h2>
                                                            ${totalDebtAmount}
                                                        </h2>
                                                        {shouldShowPercentageChange(debtPercentageChange, newDebtTotal, oldDebtData?.oldTotalDebtupdated === true) && (
                                                            <span className={debtPercentageChange > 0 ? 'price-up' : 'price-down'}>
                                                                {debtPercentageChange.toFixed(2)}%
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                                {subscribed && !hasDebtData && (
                                                    <div className="ast-mini-card-placeholder">
                                                        <img height={38} width={38} src={AmountPlaceholder} alt="" />
                                                    </div>
                                                )}
                                                {!subscribed && <h2>$0.00</h2>}
                                            </div>
                                            {!subscribed && (
                                                <NonSubLockBox />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-12">
                                    <div className="dashboard-tabs-container">
                                        <div className="dashboard-tabs-header mb-3">
                                            {tabs.map((tab) => (
                                                <button
                                                    key={tab}
                                                    className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                                                    onClick={() => setActiveTab(tab)}
                                                >
                                                    {tab}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Tab Content */}
                                        <div className="dashboard-tabs-content">
                                            {/* Overview Content */}
                                            {activeTab === "Overview" &&
                                                <div className="row">

                                                    <div className="col-md-4">
                                                        <div className="ast-card mb-4 plaid-activity-card">
                                                            <h4 className="ast-card-heading">
                                                                <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M14.5267 0.439167C14.4548 0.266086 14.3173 0.128545 14.1442 0.0566666C14.059 0.0203709 13.9676 0.00111916 13.875 0H10.3334C10.1455 0 9.96534 0.0746279 9.8325 0.207466C9.69967 0.340304 9.62504 0.520472 9.62504 0.708333C9.62504 0.896195 9.69967 1.07636 9.8325 1.2092C9.96534 1.34204 10.1455 1.41667 10.3334 1.41667H12.168L8.20837 5.37625L5.87795 3.03875C5.81211 2.97236 5.73376 2.91966 5.64745 2.8837C5.56113 2.84774 5.46855 2.82923 5.37504 2.82923C5.28153 2.82923 5.18895 2.84774 5.10263 2.8837C5.01631 2.91966 4.93797 2.97236 4.87212 3.03875L0.622121 7.28875C0.55573 7.3546 0.503035 7.43294 0.467073 7.51926C0.431112 7.60557 0.412598 7.69816 0.412598 7.79167C0.412598 7.88517 0.431112 7.97776 0.467073 8.06407C0.503035 8.15039 0.55573 8.22873 0.622121 8.29458C0.68797 8.36097 0.766313 8.41367 0.85263 8.44963C0.938947 8.48559 1.03153 8.50411 1.12504 8.50411C1.21855 8.50411 1.31113 8.48559 1.39745 8.44963C1.48376 8.41367 1.56211 8.36097 1.62795 8.29458L5.37504 4.54042L7.70545 6.87792C7.7713 6.94431 7.84965 6.997 7.93596 7.03296C8.02228 7.06892 8.11486 7.08744 8.20837 7.08744C8.30188 7.08744 8.39446 7.06892 8.48078 7.03296C8.5671 6.997 8.64544 6.94431 8.71129 6.87792L13.1667 2.41542V4.25C13.1667 4.43786 13.2413 4.61803 13.3742 4.75087C13.507 4.8837 13.6872 4.95833 13.875 4.95833C14.0629 4.95833 14.2431 4.8837 14.3759 4.75087C14.5087 4.61803 14.5834 4.43786 14.5834 4.25V0.708333C14.5823 0.61577 14.563 0.524324 14.5267 0.439167Z" fill="#979797" />
                                                                </svg>
                                                                Recent Activity
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    delay={{ show: 250, hide: 400 }}
                                                                    overlay={renderTooltip}
                                                                >
                                                                    <img className="ml-2" width={16} src={helpIcon} />
                                                                </OverlayTrigger>
                                                            </h4>
                                                            <div className="mini-scroll-bar">
                                                                {subscribed && plaidActivities.length > 0 && (
                                                                    plaidActivities.map((activity, index) => (
                                                                        <div className={`single-plaid-activity ${activity.type}`} key={activity}>
                                                                            <div>
                                                                                <h6
                                                                                    className="plaid-activity-title"
                                                                                    title={activity.title}
                                                                                >
                                                                                    {activity?.title?.length > 30
                                                                                        ? activity.title.slice(0, 30) + "..."
                                                                                        : activity.title}
                                                                                </h6>
                                                                                <p className="plaid-activity-description">{activity.description}</p>
                                                                            </div>
                                                                            {/* Dynamically add the class based on the transaction type */}
                                                                            <div className={`plaid-activity-amount ${activity.type}`}>
                                                                                {(() => {
                                                                                    const hasAmount = activity?.amount !== undefined && activity?.amount !== null;
                                                                                    if (!hasAmount) return "";
                                                                                    const amountSign = activity.amount < 0 ? "-" : "";
                                                                                    return `${amountSign}$${Math.abs(activity.amount)}`;
                                                                                })()}


                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                                {subscribed && plaidActivities.length === 0 && (
                                                                    <div className="ast-card-placeholder">
                                                                        <img
                                                                            src={RecentActivityPlaceholder}
                                                                            alt="No activities available"
                                                                        />
                                                                    </div>
                                                                )}
                                                                {!subscribed && (
                                                                    <>
                                                                        <div className="single-plaid-activity">
                                                                            <div>
                                                                                <h6 className="plaid-activity-title">Plaid Transaction Title</h6>
                                                                                <p className="plaid-activity-description">Plaid Transaction Description</p>
                                                                            </div>
                                                                            <div className="plaid-activity-amount">$0.00</div>
                                                                        </div>
                                                                        <div className="single-plaid-activity">
                                                                            <div>
                                                                                <h6 className="plaid-activity-title">Plaid Transaction Title</h6>
                                                                                <p className="plaid-activity-description">Plaid Transaction Description</p>
                                                                            </div>
                                                                            <div className="plaid-activity-amount">$0.00</div>
                                                                        </div>
                                                                        <div className="single-plaid-activity">
                                                                            <div>
                                                                                <h6 className="plaid-activity-title">Plaid Transaction Title</h6>
                                                                                <p className="plaid-activity-description">Plaid Transaction Description</p>
                                                                            </div>
                                                                            <div className="plaid-activity-amount">$0.00</div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                            {!subscribed && (
                                                                <NonSubLockBox />
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="col-md-4">
                                                        <Link to="/q7"
                                                            state={{ fromUserQuestionnaire: true }}
                                                        >
                                                            <div className="ast-card mb-4 plaid-financial-goals-card">
                                                                <h4 className="ast-card-heading">
                                                                    <i className="iconoir-leaderboard-star"></i>
                                                                    Financial Goals
                                                                </h4>
                                                                <div className="mini-scroll-bar">
                                                                    {subscribed && financialGoals.length > 0 && (
                                                                        financialGoals.map((goal, index) => {
                                                                            

                                                                            return (
                                                                                <div className="single-plaid-financial-goal" key={goal}>
                                                                                    <div className="d-flex justify-content-between">
                                                                                        <h6 className="plaid-financial-goal-title">{goal}</h6>
                                                                                        
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })
                                                                    )}
                                                                    {subscribed && financialGoals.length === 0 && (
                                                                        <div className="ast-card-placeholder">
                                                                            <img
                                                                                src={FinancialGoalsPlaceholder}
                                                                                alt="No financial goals"
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    {!subscribed && (
                                                                        <>
                                                                            <div className="single-plaid-financial-goal">
                                                                                <div className="d-flex justify-content-between">
                                                                                    <h6 className="plaid-financial-goal-title">Plaid Financial Goal Title</h6>
                                                                                    <small className="plaid-financial-goal-amount">$0.00 / $0.00</small>
                                                                                </div>
                                                                                <div className="progress">
                                                                                    <div className="progress-bar" role="progressbar" style={{ width: "25%" }} aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"
                                                                                    ></div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="single-plaid-financial-goal">
                                                                                <div className="d-flex justify-content-between">
                                                                                    <h6 className="plaid-financial-goal-title">Plaid Financial Goal Title</h6>
                                                                                    <small className="plaid-financial-goal-amount">$0.00 / $0.00</small>
                                                                                </div>
                                                                                <div className="progress">
                                                                                    <div className="progress-bar" role="progressbar" style={{ width: "50%" }} aria-valuenow="50" aria-valuemin="0" aria-valuemax="100"
                                                                                    ></div>
                                                                                </div>
                                                                            </div>
                                                                            <div className="single-plaid-financial-goal">
                                                                                <div className="d-flex justify-content-between">
                                                                                    <h6 className="plaid-financial-goal-title">Plaid Financial Goal Title</h6>
                                                                                    <small className="plaid-financial-goal-amount">$0.00 / $0.00</small>
                                                                                </div>
                                                                                <div className="progress">
                                                                                    <div className="progress-bar" role="progressbar" style={{ width: "75%" }} aria-valuenow="75" aria-valuemin="0" aria-valuemax="100"
                                                                                    ></div>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                {!subscribed && (
                                                                    <NonSubLockBox />
                                                                )}
                                                            </div>
                                                        </Link>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="ast-card mb-4">
                                                            <Link to="/q1"
                                                                state={{ fromUserQuestionnaire: true }}
                                                                style={{ textDecoration: 'none', color: 'inherit' }}
                                                                className="flex-fill">

                                                                <h4 className="ast-card-heading mb-3">
                                                                    <i className="iconoir-white-flag"></i>
                                                                    Top Priorities
                                                                </h4>
                                                                <div style={{ minHeight: 'calc( 100vh - 550px)' }}>
                                                                    {renderTopPrioritiesContent(onboardingDetails, othersData, quickSetup, handleFullSetupQuestionnaire)}
                                                                </div>
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            {/* Investment & Debts Content */}
                                            {activeTab === "Investment & Debts" &&
                                                <div className="row">
                                                    {/* Assets Card */}
                                                    <div className="col-6">
                                                        <Link to="/q6"
                                                            state={{ fromUserQuestionnaire: true }}
                                                            style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            <div className="ast-card mb-4">
                                                                <h4 className="ast-card-heading mb-3">
                                                                    <i className="iconoir-piggy-bank"></i>
                                                                    Assets Allocation
                                                                </h4>
                                                                <div>
                                                                    {renderAssetsBreakdownContent(
                                                                        subscribed,
                                                                        hasInvestmentBreakdownData,
                                                                        onboardingDetails,
                                                                        investmentChartData,
                                                                        pieChartOptions,
                                                                        calculateTotalInvestment
                                                                    )}
                                                                </div>
                                                                {!subscribed && (
                                                                    <NonSubLockBox />
                                                                )}
                                                            </div>
                                                        </Link>
                                                    </div>


                                                    {/* Debt Card */}
                                                    <div className="col-6">
                                                        <Link to="/q5"
                                                            state={{ fromUserQuestionnaire: true }}
                                                            style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            <div className="ast-card mb-4">
                                                                <h4 className="ast-card-heading mb-3">
                                                                    <i className="iconoir-dollar-circle"></i>
                                                                    Debt Breakdown
                                                                </h4>
                                                                <div>
                                                                    {renderDebtBreakdownContent(
                                                                        subscribed,
                                                                        hasDebtBreakdownData,
                                                                        onboardingDetails,
                                                                        debtChartData,
                                                                        investmentChartData,
                                                                        pieChartOptions,
                                                                        calculateTotalDebt
                                                                    )}
                                                                </div>
                                                                {!subscribed && (
                                                                    <NonSubLockBox />
                                                                )}
                                                            </div>
                                                        </Link>
                                                    </div>

                                                </div>
                                            }

                                            {/* Goals Content */}
                                            {activeTab === "Goals" &&
                                                <div className="row">
                                                    {/* Short Term Plans card */}
                                                    <div className="col-6">
                                                        <Link to="/q3"
                                                            state={{ fromUserQuestionnaire: true, option: "Short-term" }}
                                                            style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            <div className="ast-card mb-4">
                                                                <h4 className="ast-card-heading mb-3">
                                                                    <i className="iconoir-calendar"></i>
                                                                    Short Term Plans
                                                                </h4>
                                                                <div className={`db-vertical-options selected-short-terms mini-scroll-bar ${(onboardingDetails?.["Short-term"]?.length <= 0) ? 'empty-options' : ''}`}>
                                                                    {renderPlanOptionsContent(onboardingDetails, othersData, "Short-term", quickSetup, handleFullSetupQuestionnaire)}
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>

                                                    {/* Long Term Plans card */}
                                                    <div className="col-6">
                                                        <Link to="/q3"
                                                            state={{ fromUserQuestionnaire: true, option: "Long-term" }}
                                                            style={{ textDecoration: 'none', color: 'inherit' }}>
                                                            <div className="ast-card mb-4">
                                                                <h4 className="ast-card-heading mb-3">
                                                                    <i className="iconoir-calendar"></i>
                                                                    Long Term Plans
                                                                </h4>
                                                                <div className={`db-vertical-options selected-long-terms mini-scroll-bar ${(onboardingDetails?.["Long-term"]?.length <= 0) ? 'empty-options' : ''}`}>
                                                                    {renderPlanOptionsContent(onboardingDetails, othersData, "Long-term", quickSetup, handleFullSetupQuestionnaire)}
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>

                                                </div>
                                            }

                                            {/* Insights Content */}
                                            {activeTab === "Insights" &&
                                                <InsightsPanel
                                                    financialInsights={financialInsights}
                                                    subscribed={subscribed}
                                                />
                                            }
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>
                    ))}
                    {shouldShowQuestionnaireDetails && (
                            <UserQuestionnaireDtls />
                        )}
                    <DashboardCharts chartType={currentChartType} investmentData={investmentChartData}
                        oldInvestmentData={oldInvestmentChartData}
                        debtData={debtChartData}
                        oldDebtData={oldDebtChartData} popupShow={popupShow} popupClose={closePopup} />
                </div>)}

            <Modal
                size="md"
                className="askQuestionPopup customMiniPopup"
                show={askQuestionPopupShow}
                onHide={askQuestionPopupClose}
                aria-labelledby="askQuestionPopupLabel"
                backdrop="static"
                keyboard={false}
                centered
            >
                <div className="modal-body">
                    <div className="d-flex justify-content-between">
                        <h5 className="f-500 mt-2">Let’s get in touch</h5>
                        <Link className="close-popup-btn" onClick={askQuestionPopupClose}>
                            <i className="iconoir-xmark"></i>
                        </Link>
                    </div>
                    <p className="text-14">Want your advisor to take a look at something? You can upload documents here — like pay stubs, tax returns, statements, or anything affecting your money.</p>
                    <div className="form-group">
                        <textarea
                            value={messageInput}
                            onChange={(e) => setMessageInput(e.target.value)}
                            placeholder="Send Message"
                            className="form-control"
                            rows="7"
                            disabled={isUploading}
                        />
                    </div>

                    {attachedFiles.length > 0 && (
                        <div className="chat-uploaded-document" style={{ position: 'relative', marginBottom: '15px', bottom: '0', left: '0', boxShadow: 'unset', borderRadius: '3px' }}>
                            <div className="chat-uploaded-document-header">
                                <span className="chat-uploaded-document-title">
                                    {getAttachedFilesText(attachedFiles.length)}
                                </span>
                            </div>
                            <div className="chat-uploaded-document-list">
                                {attachedFiles.map((file, index) => (
                                    <div key={file} className="chat-uploaded-document-item">
                                        <div className="chat-uploaded-document-name" title={file.name}>
                                            {file.name.length > 30 ? `${file.name.slice(0, 30)}...` : file.name}
                                        </div>
                                        {isUploading ? (
                                            <div className="chat-uploaded-document-progress">
                                                <div className="circular-progress"></div>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="chat-uploaded-document-remove"
                                                onClick={() => handleRemoveAttachedFile(index)}
                                                title="Remove file"
                                            >
                                                <i className="iconoir-xmark"></i>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="d-flex justify-content-between place-items-center">
                        <small className="text-grey"><i>Your advisor typically responds within 1 business day.</i></small>
                        <div className="d-flex gap-10 justify-content-end mt-2 mb-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.csv,.doc,.docx"
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                            />
                            <button
                                className="as-btn-primary align-items-center gap-2 px-3"
                                type="button"
                                onClick={handleAttachClick}
                                disabled={isUploading}
                            >
                                <i className="iconoir-attachment"></i>
                            </button>
                            <button
                                className="as-btn-primary d-flex align-items-center gap-5 px-4"
                                onClick={handleSendMessage}
                                disabled={isUploading}
                            >
                                {renderAskAdvisorSendButtonContent(isUploading)}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* thank you popup */}
            {<Modal
                size="md"
                className="thankYouPopup"
                show={thankYouPopupShow}
                onHide={handleModalClose}
                aria-labelledby="thankYouPopupLabel"
                backdrop="static"
                keyboard={false}
                centered
            >
                <div className="modal-body">
                    <div className="d-flex justify-content-end">
                        <Link
                            className="close-popup-btn"
                            onClick={handleModalClose}
                        >
                            <i className="fa fa-times"></i>
                        </Link>
                    </div>

                    <h4 className="text-center">Thank you! Your meeting is confirmed.</h4>
                    <p className="mb-4 text-center">
                        Email sent! Check your inbox for detailed information!
                    </p>
                    <div className="appointment-dtls-box">
                        <div className="mb-4">
                            <p className="mb-1">You're meeting with</p>
                            <strong>{appointmentDetails?.advisor?.firstName} {appointmentDetails?.advisor?.lastName}</strong>
                        </div>

                        <div className="row appointment-dtls mb-3">
                            <div className="col-5 d-flex place-items-center">
                                <img src={dateIcon} alt="" />
                                <p className="mb-0 ml-2 f-500">Date & Time</p>
                            </div>
                            <div className="col-7 place-content-center">
                                <p>{moment.utc(appointmentDetails?.appointment?.date).format('LL')}, {appointmentDetails?.appointment?.time}</p>
                            </div>
                        </div>
                        <div className="row appointment-dtls">
                            <div className="col-5 d-flex place-items-center">
                                <img src={detailsIcon} alt="" />
                                <p className="mb-0 ml-2 f-500">Details</p>
                            </div>
                            <div className="col-7 place-content-center">
                                <p>We’ve sent and email with your booking details</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>}
        </>


    );
}

export default Dashboard;